import UdeskLocales from 'UdeskLocales';
import React, { FC, memo, useEffect, useMemo, useState } from 'react';
import { Button, Space, Switch } from 'udesk-ui';
import {
    Wrapper,
    Content,
    Item,
    Index,
    Config,
    Label,
    Remove,
    Add,
    Action,
} from './styledComponents';
import LanguageSelect from 'Component/pages/components/LanguageSelect';
import ASRModelSelect from 'src/components/pages/components/ASRModelSelect';
import CustomFilter from 'Component/pages/components/CustomFilter';
import { AddCircleFilled, IndeterminateCircleFilled } from '@udesk/icons';
import { BaseFilter1 } from 'src/api/types';
import { SelectProps } from 'antd';

interface ValueItem extends BaseFilter1 {
    languageChangeDisabled?: boolean;
}

export interface TemplateProps {
    value?: ValueItem[];
    onChange?: (v: TemplateProps['value']) => void;
    type: number;
    querryType?: string;
    filterList?: string[];
    disabledList?: number[];
    overrideList?: number[];
    options?: SelectProps<number>['options'];

    onSave?: (index: number, v: TemplateProps['value']) => void;
    onDelete?: (idx: number) => void;

    hasAdaption?: boolean;
    hasASR?: boolean;
}

const Template: FC<TemplateProps> = memo((props) => {
    const {
        value,
        onChange,
        type,
        querryType,
        filterList,
        disabledList,
        onSave: _onSave,
        onDelete,
        hasAdaption,
        hasASR,
        options,
    } = props;

    const mainLanOptions = useMemo(() => {
        return options?.map((item) => {
            return {
                ...item,
                disabled: disabledList?.includes(item.value),
            };
        });
    }, [disabledList, options]);

    const [items, setItems] = useState<TemplateProps['value']>();

    useEffect(() => {
        setItems(value);
    }, [value]);

    const onAdd = () => {
        setItems((v) => {
            const _v = [...(v || []), {}];
            onChange?.(_v);
            return _v;
        });
    };

    const onRemove = (index: number) => {
        // 这里，如果存在外部删除方法，那么把控制交给外部
        if (onDelete) {
            onDelete?.(index);
        } else {
            setItems((v) => {
                const _v = v?.filter((_, idx) => idx !== index);
                onChange?.(_v);
                return _v;
            });
        }
    };

    const onLanguageChange = (languageType: number, index: number) => {
        setItems((v) => {
            const _v = v?.map((__v, idx) => {
                if (idx === index) {
                    return {
                        ...__v,
                        languageType,
                        multiLanguageTypeList: undefined,
                        asrEngineType: undefined,
                    };
                } else {
                    return __v;
                }
            });
            onChange?.(_v);
            return _v;
        });
    };

    const onAdaptionEnabledChange = (adaptionEnabled: boolean, index: number) => {
        setItems((v) => {
            const _v = v?.map((__v, idx) => {
                if (idx === index) {
                    return {
                        ...__v,
                        autoDetection: adaptionEnabled ? 1 : 0,
                    };
                } else {
                    return __v;
                }
            });
            onChange?.(_v);
            return _v;
        });
    };

    const onSecondaryLanguageChange = (multiLanguageTypeList: number[], index: number) => {
        setItems((v) => {
            const _v = v?.map((__v, idx) => {
                if (idx === index) {
                    return {
                        ...__v,
                        multiLanguageTypeList,
                    };
                } else {
                    return __v;
                }
            });
            onChange?.(_v);
            return _v;
        });
    };

    const onASRModelChange = (asrEngineType: number, index: number) => {
        setItems((v) => {
            const _v = v?.map((__v, idx) => {
                if (idx === index) {
                    return {
                        ...__v,
                        asrEngineType,
                    };
                } else {
                    return __v;
                }
            });
            onChange?.(_v);
            return _v;
        });
    };

    const onFilterChange = (
        value:
            | {
                  conditionList?: any;
                  customJudgeLogic?: any;
                  judgeStrategy?: any;
              }
            | undefined,
        index: number
    ) => {
        setItems((v) => {
            const _v = v?.map((__v, idx) => {
                if (idx === index) {
                    return {
                        ...__v,
                        ...value,
                    };
                } else {
                    return __v;
                }
            });
            onChange?.(_v);
            return _v;
        });
    };

    const onSave = (index: number) => {
        _onSave?.(index, items);
    };

    return (
        <Wrapper>
            <Content>
                {items?.map((item, index) => {
                    const {
                        languageType,
                        multiLanguageTypeList,
                        languageChangeDisabled,
                        autoDetection,
                        asrEngineType,
                        ...rest
                    } = item || {};

                    const secondLanOptions = options?.map((item) => {
                        return {
                            ...item,
                            disabled: (languageType ? [languageType] : []).includes(item.value),
                        };
                    });

                    return (
                        <Item>
                            <Index title={index + 1}>
                                {/* 规则 */}
                                {UdeskLocales['current'].components.languageSetting.index.rules}
                                {index + 1}
                            </Index>

                            <Config>
                                <Space>
                                    <Label>
                                        {hasAdaption
                                            ? /* 主语言 */ UdeskLocales['current'].components
                                                  .languageSetting.index.mainLanguage
                                            : /* 语言类型： */ UdeskLocales['current'].components
                                                  .languageSetting.index.languageType}
                                    </Label>
                                    <LanguageSelect
                                        value={languageType}
                                        onChange={(v) => onLanguageChange(v as number, index)}
                                        disabled={languageChangeDisabled}
                                        disabledList={disabledList}
                                        style={{ width: 300 }}
                                        options={mainLanOptions}
                                    />
                                </Space>

                                {hasAdaption ? (
                                    <>
                                        <Space>
                                            <Label>
                                                {/* 语言自适应 */}
                                                {
                                                    UdeskLocales['current'].components
                                                        .languageSetting.index.languageAdaptive
                                                }
                                            </Label>
                                            <Switch
                                                checked={autoDetection === 1}
                                                onChange={(v) => onAdaptionEnabledChange(v, index)}
                                            />
                                        </Space>

                                        {autoDetection === 1 ? (
                                            <Space>
                                                <Label>
                                                    {/* 副语言 */}
                                                    {
                                                        UdeskLocales['current'].components
                                                            .languageSetting.index.paraLanguage
                                                    }
                                                </Label>
                                                <LanguageSelect
                                                    mode="multiple"
                                                    maxTagCount={3}
                                                    value={multiLanguageTypeList || []}
                                                    onChange={(v) =>
                                                        onSecondaryLanguageChange(
                                                            v as number[],
                                                            index
                                                        )
                                                    }
                                                    disabledList={
                                                        languageType ? [languageType] : []
                                                    }
                                                    disabledAllListWithoutChecked={
                                                        (multiLanguageTypeList?.length || 0) >= 9
                                                    }
                                                    style={{ width: 300 }}
                                                    options={secondLanOptions}
                                                />
                                            </Space>
                                        ) : null}
                                    </>
                                ) : null}

                                {hasASR ? (
                                    <Space>
                                        <Label>
                                            {/* ASR模型 */}
                                            {
                                                UdeskLocales['current'].components.languageSetting
                                                    .index.aSRModel
                                            }
                                        </Label>
                                        <ASRModelSelect
                                            languageType={languageType}
                                            value={asrEngineType}
                                            onChange={(v) => onASRModelChange(v, index)}
                                        />
                                    </Space>
                                ) : null}

                                <CustomFilter
                                    type={type}
                                    querryType={querryType}
                                    filterList={filterList}
                                    value={rest}
                                    onChange={(value) => onFilterChange(value, index)}
                                />

                                <Action>
                                    <Button
                                        type="primary"
                                        onClick={() => onSave(index)}
                                        size="small"
                                    >
                                        {/* 保存 */}
                                        {
                                            UdeskLocales['current'].components.languageSetting.index
                                                .preserve
                                        }
                                    </Button>
                                </Action>
                            </Config>

                            <Remove>
                                <Button
                                    icon={<IndeterminateCircleFilled />}
                                    type="link"
                                    onClick={() => onRemove(index)}
                                >
                                    {/* 移除 */}
                                    {
                                        UdeskLocales['current'].components.languageSetting.index
                                            .remove
                                    }
                                </Button>
                            </Remove>
                        </Item>
                    );
                })}
            </Content>

            <Add>
                <Button
                    icon={<AddCircleFilled />}
                    type="link"
                    onClick={() => onAdd()}
                    style={{ padding: 0 }}
                >
                    {/* 新建 */}
                    {UdeskLocales['current'].components.languageSetting.index.new}
                </Button>
            </Add>
        </Wrapper>
    );
});

export default Template;
